import AgSummaryCards, { ToastListProps } from '../../../stories/Components/Cards/AgSummaryCards';
import edit_square from '../../../stories/assets/edit_square.svg';
import AgTypography from '../../../stories/Styles/AgTypography';
import { useIntl } from 'react-intl';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  ApiCountry,
  ApiIdentityOut,
  ApiOrganizationProfile, ApiUserInfo
} from '@agunity/api-v4/build/data-contracts';
import { setStaff_CountryOptions } from '../../../redux/slices/staff';
import {
  MANDATORY_LEVEL,
  TEMPLATE_GENDER
} from '../../../Constants';
import { getCountries } from '../../../redux/slices/countries';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { ProfileSectionTableData } from '../../../stories/Components/AgSimpleTable';
import { selectLocaleOptions } from '../../../redux/slices/locales';
import { ValidationListPersonalProps } from './AddStaff';
import AgRoleChip from '../../../stories/Chips/AgRoleChips';

export interface SummaryCardPageProps {
  setSelectedCard?: Dispatch<SetStateAction<number>>;
  personalDetails?: ApiIdentityOut;
  orgData?: ApiOrganizationProfile;
  staffData?: ApiUserInfo;
  numberOfPersonalMandatoryCompleted?: number;
  numberOfStaffMandatoryCompleted?: number;
  numberOfPersonalMandatoryAll?: number;
  numberOfStaffMandatoryAll?: number;
  missingMandatoryFields_personal?: string[];
  missingMandatoryFields_staff?: string[];
  ageUnknown?: boolean;
  validationListPersonal?: ValidationListPersonalProps;
  mandatoryMessagesHandledSeparately?: string[];
  otherErrorsExceptForMandatoryFields?: string[];
}

const SummaryCardPage = (
  {
    setSelectedCard,
    personalDetails,
    orgData,
    staffData,
    numberOfPersonalMandatoryCompleted,
    numberOfStaffMandatoryAll,
    numberOfStaffMandatoryCompleted,
    numberOfPersonalMandatoryAll,
    missingMandatoryFields_personal,
    missingMandatoryFields_staff,
    ageUnknown,
    validationListPersonal,
    mandatoryMessagesHandledSeparately,
    otherErrorsExceptForMandatoryFields
  }: SummaryCardPageProps
) => {
  const messages = useIntl().formatMessage;
  const dispatch = useAppDispatch();
  const nameCheck = useAppSelector(state => state.staff.nameCheck);
  // const emailCheck = useAppSelector(state => state.staff.emailExists);
  const localeOptions = useAppSelector(selectLocaleOptions) || [];
  const localeName = localeOptions.find(locale => locale.locale?.toUpperCase() === personalDetails?.locale.toUpperCase())?.name || '';

  const [countries, setCountries] = useState<ApiCountry[]>();
  const [selectedCountry, setSelectedCountry] = useState<ApiCountry>();
  const concatMissingMandatoryFields = (missingMandatoryFields?: string[]) => {// as per design, 'mandatory errors' and other errors are handled differently
    if (!missingMandatoryFields?.length) return '';
    if (missingMandatoryFields.length === 1) return `${missingMandatoryFields[0]} ${messages({ id: 'MEMBER_MISSING_FIELD_MESSAGE_SINGLE' })}`;
    return `${missingMandatoryFields.slice(0, -1).join(', ')} ${messages({ id: 'AND' })} ${missingMandatoryFields[missingMandatoryFields.length - 1]} ${messages({ id: 'MEMBER_MISSING_FIELD_MESSAGE_MULTIPLE' })}`;
  };

  const concatErrorsOtherThanMandatoryFields = (validationListPersonal?: ValidationListPersonalProps) => {
    // as per design, 'mandatory errors' and other errors are handled differently
    let result = '';
    if (!validationListPersonal) return result;
    for (const key in validationListPersonal) {
      //@ts-ignore
      validationListPersonal[key].forEach(item => {
        if (
          !mandatoryMessagesHandledSeparately?.includes(item) && // handled by concatMissingMandatoryFields function
          !(key.toUpperCase() === 'DATEOFBIRTH' && ageUnknown) // if age is unknown, then age error caption will be hidden
        ){
          result += ' ' + item;
        }
      });
    }
    return result;
  };

  useEffect(() => {
    const f = async () => {
      try {
        if (!countries || countries?.length === 0) {
          const cou = await dispatch(getCountries());
          if (cou?.data?.length) {
            const countryList = cou.data;
            setCountries(countryList);
            dispatch(setStaff_CountryOptions(cou.data));
          }
        }
        if (countries?.length && personalDetails?.address?.countryId) {
          const countryObj = countries.find(country => country.id === personalDetails?.address?.countryId);
          countryObj && setSelectedCountry(countryObj);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };
    f();
  }, [countries, dispatch, personalDetails?.address?.countryId]);

  const dateOfBirth = personalDetails?.dateOfBirth.split('T')[0] ?? '';
  // let age;
  // if (dateOfBirth) {
  //   const birthDate = new Date(dateOfBirth);
  //   const currentDate = new Date();
  //   age = Math.max(currentDate.getFullYear() - birthDate.getFullYear(), 0);
  // }
  const personalTableData: ProfileSectionTableData[] = [ //todo will clean comment out codes after QA testing
    { name: messages({ id: 'FIRSTNAME' }), value: personalDetails?.firstName ?? '' },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_COUNTRY' }),
      value: selectedCountry?.name ?? ''
    },
    // code below may become mandatory in the future
    // {
    //   name: messages({ id: 'MEMBER_PERSONAL_DETAILS_PROVINCE' }),
    //   value: personalDetails?.address?.province ?? ''
    // },
    // {
    //   name: messages({ id: 'MEMBER_PERSONAL_DETAILS_CITY' }),
    //   value: personalDetails?.address?.city ?? ''
    // },
    // {
    //   name: messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_1' }),
    //   value: personalDetails?.address?.addressLine1 ?? ''
    // },
    // {
    //   name: messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_2' }),
    //   value: personalDetails?.address?.addressLine2 ?? ''
    // },
    // {
    //   name: messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_3' }),
    //   value: personalDetails?.address?.addressLine3 ?? ''
    // },
    // { name: messages({ id: 'MEMBER_PERSONAL_DETAILS_AGE' }), value: age ?? '' },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_DATE_OF_BIRTH' }),
      value: ageUnknown
        ? messages({ id: 'UNKNOWN' })
        : dateOfBirth ?? ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_GENDER' }),
      value: personalDetails?.gender.toUpperCase() === TEMPLATE_GENDER.Male
        ? messages({ id: 'MALE' })
        : personalDetails?.gender.toUpperCase() === TEMPLATE_GENDER.Female
          ? messages({ id: 'FEMALE' })
          : personalDetails?.gender.toUpperCase() === TEMPLATE_GENDER.NonBinary
            ? messages({ id: 'NONBINARY' })
            : personalDetails?.gender.toUpperCase() === TEMPLATE_GENDER.Other
              ? messages({ id: 'NOGENDER' })
              : ''
    },
    {
      name: messages({ id: 'MEMBER_PERSONAL_DETAILS_LANGUAGE' }),
      value: localeName
    },
    // {
    //   name: messages({ id: 'MEMBER_PERSONAL_DETAILS_NOTES' }),
    //   value: personalDetails?.data?.find(item => item.key === MEMBER_FIELD_NAME_NOTES)?.value || ''
    // },
    ...(personalDetails?.address?.location?.X || personalDetails?.address?.location?.Y) ? [
      {
        name: messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LATITUDE' }),
        value: personalDetails?.address?.location?.X ?? ''
      },
      {
        name: messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LONGITUDE' }),
        value: personalDetails?.address?.location?.Y ?? ''
      }
    ] : []
    // {
    //   name: messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_ALTITUDE' }),
    //   value: personalDetails?.address?.location?.altitude ?? ''
    // },
    // {
    //   name: messages({ id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_PHONE' }),
    //   value: personalDetails?.phoneNumber ?? ''
    // },
    // {
    //   name: messages({ id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_EMAIL' }),
    //   value: personalDetails?.email ?? ''
    // }
  ];

  const staffTableData: ProfileSectionTableData[] = [ //todo will clean comment out codes after QA testing
    {
      name: messages({ id: 'MEMBER_OF' }),
      value: orgData?.data ?
        <AgRoleChip
          type={'organization'}
          message={orgData?.name || ''}
          style={{ borderRadius: '6px' }}
        />
        : ''
    },
    {
      name: messages({ id: 'ROLES' }),
      value: staffData?.roles?.length ?
        <AgTypography
          style={{
            display: 'flex',
            gap: '10px'
          }}
        >
          {staffData?.roles?.map((role, index) => {
            return (
              <AgRoleChip
                key={index}
                type={'staff'}
                message={role.name || ''}
              />
            );
          })}
        </AgTypography>
        : ''
    },

    // @ts-ignore
    ...personalDetails?.data?.filter(item => item.mandatoryLevel === MANDATORY_LEVEL.Mandatory).map(item => ({ name: item.key, value: item.value })) || [],
    // @ts-ignore
    ...orgData?.data?.filter(item => item.mandatoryLevel === MANDATORY_LEVEL.Mandatory).map(item => ({ name: item.key, value: item.value })) || []
  ];

  const personalToastList: ToastListProps[] = [
    ...(nameCheck.open) ? [{
      message: nameCheck.message,
      open: nameCheck.open
    }] : [],
    // ...(emailCheck) ? [{ // emailcheck will use error toast, so comment out here, this is a warning toast
    //   message: messages({ id: 'MEMBER_EMAIL_CHECK' }),
    //   open: emailCheck
    // }] : [],
    ...(missingMandatoryFields_personal?.length || otherErrorsExceptForMandatoryFields?.length ? [{
      message: concatMissingMandatoryFields(missingMandatoryFields_personal) + concatErrorsOtherThanMandatoryFields(validationListPersonal),
      open: true,
      isError: true
    }] : [])
  ];

  const staffToastList: ToastListProps[] = [ // todo to be updated in the following sprint
    // todo update warning toasts here
    ...(missingMandatoryFields_staff?.length ? [{
      message: concatMissingMandatoryFields(missingMandatoryFields_staff),
      open: true,
      isError: true
    }] : [])
  ];

  return (
    <AgTypography style={{ padding: '16px', display: 'grid', gap: '12px' }}>
      <AgSummaryCards
        tableData={personalTableData}
        numberOfAllFields={numberOfPersonalMandatoryAll}
        numberOfFinishedFields={numberOfPersonalMandatoryCompleted}
        title={messages({ id: 'PROFILE_PERSONAL_ACCOUNT' })}
        btnLabel={messages({ id: 'EDIT' })}
        btnIcon={<img src={edit_square} alt='' />}
        btnOnClick={() => {
          setSelectedCard && setSelectedCard(0);
        }}
        toastList={personalToastList}
        missingMandatoryFields={missingMandatoryFields_personal}
      />
      <AgSummaryCards
        tableData={staffTableData}
        numberOfAllFields={numberOfStaffMandatoryAll}
        numberOfFinishedFields={numberOfStaffMandatoryCompleted}
        title={messages({ id: 'STAFF_PROFILE' })}
        btnLabel={messages({ id: 'EDIT' })}
        btnIcon={<img src={edit_square} alt='' />}
        btnOnClick={() => {
          setSelectedCard && setSelectedCard(1);
        }}
        toastList={staffToastList}
        missingMandatoryFields={missingMandatoryFields_staff}
      />
    </AgTypography>
  );
};

export default SummaryCardPage;